// src/@shared/utils/useDriverTutorial.js
import { useEffect, useRef } from "react";
import { driver as Driver } from "driver.js";
import { useTutorial } from "./useTutorial";

/**
 * Maps your tutorial sequence into Driver.js step format.
 */
const mapStepToDriver = (sequence, currentStepIndex) => {
  const step = sequence?.steps?.[currentStepIndex];
  if (!step || !step.selector || !step.text) return [];

  return [
    {
      element: step.selector,
      popover: {
        title: step.title || "Tutorial",
        description: step.description || "",
        side: step.side || "bottom",
        align: step.align || "start",
      },
      highlightPadding: step.highlightPadding || 0,
    },
  ];
};

export const useDriverTutorial = (userId, enabled = true) => {
  const driverRef = useRef(null);
  const { progress, sequence, advanceStep } = useTutorial(userId);

  useEffect(() => {
    if (!enabled) {
      driverRef.current?.destroy();
      return;
    }

    if (!sequence || !progress || progress.currentStepIndex == null) return;

    const steps = mapStepToDriver(sequence, progress.currentStepIndex);
    if (steps.length === 0) return;

    // Wait until element is available in the DOM.
    const checkAndStart = () => {
      const el = document.querySelector(steps[0].element);
      if (!el) {
        setTimeout(checkAndStart, 100);
        return;
      }

      driverRef.current = Driver({
        steps,
        showProgress: true,
        animate: true,
        className: "driverjs-theme",
        onNext: () => advanceStep(),
        onSkip: () => {},
        allowClose: false,
        overlayClickNext: false,
        keyboardControl: false,
      });

      setTimeout(() => {
        driverRef.current?.drive();
      }, 100);
    };

    checkAndStart();

    return () => {
      driverRef.current?.destroy();
    };
  }, [enabled, progress?.currentStepIndex, sequence]);

  return {
    progress,
    sequence,
    advanceStep,
    triggerDriverStep,
  };
};

export const triggerDriverStep = (selector, options = {}, retries = 10) => {
  const el = document.querySelector(selector);

  const isVisible = (node) => {
    if (!node) return false;
    const rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  if ((!el || !isVisible(el)) && retries > 0) {
    setTimeout(() => {
      triggerDriverStep(selector, options, retries - 1);
    }, 250);
    return;
  }

  if (!el || !isVisible(el)) {
    console.warn("Element not found or not visible:", selector);
    return;
  }

  requestAnimationFrame(() => {
    const driverInstance = Driver({
      steps: [
        {
          element: selector,
          popover: {
            title: options.title || "Tutorial",
            description: options.description || "",
            side: options.side || "bottom",
            align: options.align || "center",
          },
          highlightPadding: options.highlightPadding || 0,
        },
      ],
      showProgress: true,
      animate: true,
      className: "driverjs-theme",
      onNext: options.onNext || (() => {}),
      onSkip: options.onSkip || (() => {}),
      allowClose: false,
      overlayClickNext: false,
      keyboardControl: false,
    });

    driverInstance.drive();
  });
};
